import React from 'react';

import {
  FEATURE_TOGGLES_LOCAL_STORAGE_ID,
  FEATURE_TOGGLES_QUERY_PARAM,
} from '../../constants/featureToggle';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { GlobalMessages } from '../../types/Messages';
import { featureToggleUtils } from '../../utils/featureToggles/featureToggles';

type FeatureTogglesSetters = {
  setFeatureToggles: (features: FeatureToggleValues) => void;
  setFeatureTogglesLoaded: (loaded: boolean) => void;
};

export type Props = {
  forceLoad?: boolean;
};

type UseFeatureTogglesParams = FeatureTogglesSetters & Props;

export const hooks = {
  useSubjectStates(): FeatureTogglesSetters {
    const setFeatureToggles = messageHooks.useNext(GlobalMessages.FeatureToggles);
    const setFeatureTogglesLoaded = messageHooks.useNext(GlobalMessages.FeatureTogglesLoaded);

    return {
      setFeatureToggles,
      setFeatureTogglesLoaded,
    };
  },

  useFeatureToggles({
    forceLoad,
    setFeatureToggles,
    setFeatureTogglesLoaded,
  }: UseFeatureTogglesParams) {
    React.useEffect(() => {
      const queryParams = new URLSearchParams(window.location.search);
      const userIdentifier = queryParams.get(FEATURE_TOGGLES_QUERY_PARAM)
        || localStorage.getItem(FEATURE_TOGGLES_LOCAL_STORAGE_ID)
        || '';

      if (!userIdentifier && !forceLoad) {
        return;
      }

      (async () => {
        const featureToggles = await featureToggleUtils.getFeatureTogglesFromStoryblok(
          queryParams.get(FEATURE_TOGGLES_QUERY_PARAM)
          || localStorage.getItem(FEATURE_TOGGLES_LOCAL_STORAGE_ID)
          || '',
        );

        setFeatureToggles(featureToggles);
        setFeatureTogglesLoaded(true);
      })();
    }, [forceLoad, setFeatureToggles, setFeatureTogglesLoaded]);
  },
};

export const FeatureToggleContainerStoryblok = React.memo((props: Props) => {
  const { forceLoad } = props;
  const {
    setFeatureToggles,
    setFeatureTogglesLoaded,
  } = hooks.useSubjectStates();

  hooks.useFeatureToggles({
    forceLoad,
    setFeatureToggles,
    setFeatureTogglesLoaded,
  });

  return null;
});

FeatureToggleContainerStoryblok.displayName = 'FeatureToggleContainerStoryblok';
